/**
 * Lot 4B/4C — helpers formulaire Admin Cartes cadeaux (purs).
 */

export const PRICING_FIXED = 'FIXED_AMOUNTS';
export const PRICING_FREE = 'FREE_AMOUNT';

export const VALIDITY_PRESETS = {
  NONE: { id: 'NONE', label: 'Sans expiration', days: null },
  M6: { id: 'M6', label: '6 mois', days: 183 },
  M12: { id: 'M12', label: '12 mois', days: 365 },
  M24: { id: 'M24', label: '24 mois', days: 730 },
  CUSTOM: { id: 'CUSTOM', label: 'Personnalisée', days: null },
};

export function emptyGiftCardTemplateForm(overrides = {}) {
  return {
    nom: '',
    description: '',
    imageUrl: '',
    pricingMode: PRICING_FREE,
    amountEurosList: ['20', '50', '100'],
    minAmountEuros: '20',
    maxAmountEuros: '500',
    validityPreset: 'M12',
    customValidityDays: '',
    actif: false,
    catalogVisible: false,
    ...overrides,
  };
}

export function formFromTemplate(template) {
  if (!template) return emptyGiftCardTemplateForm();
  let validityPreset = 'NONE';
  let customValidityDays = '';
  const d = template.defaultValidityDays;
  if (d == null) {
    validityPreset = 'NONE';
  } else if (d === 183) {
    validityPreset = 'M6';
  } else if (d === 365) {
    validityPreset = 'M12';
  } else if (d === 730) {
    validityPreset = 'M24';
  } else {
    validityPreset = 'CUSTOM';
    customValidityDays = String(d);
  }

  return {
    nom: template.nom || '',
    description: template.description || '',
    imageUrl: template.imageUrl || '',
    pricingMode:
      template.pricingMode === PRICING_FIXED ? PRICING_FIXED : PRICING_FREE,
    amountEurosList:
      template.pricingMode === PRICING_FIXED && Array.isArray(template.amounts)
        ? template.amounts
            .slice()
            .sort((a, b) => a.ordre - b.ordre)
            .map((a) => (Number(a.amountCents) / 100).toFixed(2).replace(/\.00$/, ''))
        : ['20', '50', '100'],
    minAmountEuros:
      template.minAmountCents != null
        ? String(Number(template.minAmountCents) / 100)
        : '20',
    maxAmountEuros:
      template.maxAmountCents != null
        ? String(Number(template.maxAmountCents) / 100)
        : '500',
    validityPreset,
    customValidityDays,
    actif: Boolean(template.actif),
    catalogVisible: Boolean(template.catalogVisible),
  };
}

function parseEurosField(raw, field, errors) {
  const s = String(raw ?? '').trim().replace(',', '.');
  if (!s) {
    errors[field] = 'Requis';
    return null;
  }
  if (!/^\d+(\.\d{1,2})?$/.test(s)) {
    errors[field] = 'Nombre positif, max 2 décimales';
    return null;
  }
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) {
    errors[field] = 'Doit être > 0';
    return null;
  }
  return n;
}

export function resolveValidityDays(form) {
  const preset = form.validityPreset || 'NONE';
  if (preset === 'NONE') return null;
  if (preset === 'M6') return 183;
  if (preset === 'M12') return 365;
  if (preset === 'M24') return 730;
  const n = Number(String(form.customValidityDays || '').trim());
  if (!Number.isInteger(n) || n < 1 || n > 3650) {
    return undefined; // invalide
  }
  return n;
}

/**
 * @returns {{ ok: boolean, errors: Record<string,string>, values?: object }}
 */
export function validateGiftCardTemplateForm(form) {
  const errors = {};
  const nom = String(form.nom || '').trim();
  if (!nom) errors.nom = 'Nom requis';

  const pricingMode = form.pricingMode === PRICING_FIXED ? PRICING_FIXED : PRICING_FREE;
  let amounts;
  let minAmountEuros;
  let maxAmountEuros;

  if (pricingMode === PRICING_FIXED) {
    const list = Array.isArray(form.amountEurosList) ? form.amountEurosList : [];
    if (list.length < 1) {
      errors.amounts = 'Au moins un montant';
    } else {
      const parsed = [];
      const seen = new Set();
      list.forEach((raw, i) => {
        const euros = parseEurosField(raw, `amount_${i}`, errors);
        if (euros == null) return;
        if (seen.has(euros)) {
          errors.amounts = 'Montants en doublon';
          return;
        }
        seen.add(euros);
        parsed.push({ amountEuros: euros, ordre: i });
      });
      amounts = parsed;
      if (!errors.amounts && parsed.length < 1) {
        errors.amounts = 'Au moins un montant valide';
      }
    }
  } else {
    minAmountEuros = parseEurosField(form.minAmountEuros, 'minAmountEuros', errors);
    maxAmountEuros = parseEurosField(form.maxAmountEuros, 'maxAmountEuros', errors);
    if (
      minAmountEuros != null &&
      maxAmountEuros != null &&
      minAmountEuros > maxAmountEuros
    ) {
      errors.maxAmountEuros = 'Le maximum doit être ≥ minimum';
    }
  }

  const defaultValidityDays = resolveValidityDays(form);
  if (defaultValidityDays === undefined) {
    errors.customValidityDays = 'Jours invalides (1–3650)';
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }

  const values = {
    nom,
    description: String(form.description || '').trim() || null,
    imageUrl: String(form.imageUrl || '').trim() || null,
    pricingMode,
    actif: Boolean(form.actif),
    catalogVisible: Boolean(form.catalogVisible),
    defaultValidityDays: defaultValidityDays ?? null,
  };

  if (pricingMode === PRICING_FIXED) {
    values.amounts = amounts;
  } else {
    values.minAmountEuros = minAmountEuros;
    values.maxAmountEuros = maxAmountEuros;
  }

  return { ok: true, errors: {}, values };
}

export function buildGiftCardTemplateApiPayload(form) {
  const { ok, values, errors } = validateGiftCardTemplateForm(form);
  if (!ok) return { ok: false, errors };
  return { ok: true, payload: values };
}

export function formatAmountCentsEur(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    n / 100,
  );
}

export function formatPricingSummary(template) {
  if (!template) return '—';
  if (template.pricingMode === PRICING_FIXED) {
    const amounts = (template.amounts || [])
      .slice()
      .sort((a, b) => a.ordre - b.ordre)
      .map((a) => formatAmountCentsEur(a.amountCents));
    return amounts.length ? amounts.join(' · ') : 'Montants fixes';
  }
  return `${formatAmountCentsEur(template.minAmountCents)} – ${formatAmountCentsEur(template.maxAmountCents)}`;
}

export function formatValidityLabel(days) {
  if (days == null) return 'Sans expiration';
  if (days === 183) return '6 mois';
  if (days === 365) return '12 mois';
  if (days === 730) return '24 mois';
  return `${days} jours`;
}

export function getErrorMessage(err, fallback = 'Erreur') {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.error?.message ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

export function getErrorStatus(err) {
  return err?.response?.status || err?.status || null;
}
